import { Alert, Button, Card, DatePicker, Empty, Space, Table, Tag, Typography } from "@arco-design/web-react";
import type { ColumnProps } from "@arco-design/web-react/es/Table";
import { Download, Search } from "lucide-react";
import { canUsePermission } from "../../../lib/auth";
import { useCurrentAuthUser } from "../../auth";
import { SALES_PERFORMANCE_EXPORT } from "../config";
import { useSalesPerformanceRuntime } from "../runtime";
import type { PerformanceRow } from "../types";
import { SalesPerformanceDetails } from "./SalesPerformanceDetails";
import "./salesPerformance.css";

/** 月份查询与十一项指标展示，导出沿用已查询月份。 */
export function SalesPerformanceFeaturePage() {
  const { data: user } = useCurrentAuthUser();
  const canExport = canUsePermission(user, SALES_PERFORMANCE_EXPORT);
  const runtime = useSalesPerformanceRuntime(canExport);
  const { query } = runtime;
  const report = query.isError || query.isFetching ? undefined : query.data;
  const columns: ColumnProps<PerformanceRow>[] = [
    { title: "销售", dataIndex: "user_name", width: 130, fixed: "left" },
    ...(query.data?.columns ?? []).map((column) => ({
      title: column.label, dataIndex: column.key, align: "center" as const,
      width: column.key === "profile_fields" ? 230 : 105,
      render: (value: number, row: PerformanceRow) => <Button type="text" size="mini"
        className={value === 0 ? "sales-performance-zero" : undefined}
        aria-label={`查看${row.user_name}的${column.label}明细`}
        onClick={() => runtime.openDetails(row.user_id, row.user_name, column.key, column.label)}>{value}</Button>,
    })),
  ];
  return (
    <section className="sales-performance-page">
      <Card bordered={false}>
        <div className="sales-performance-filter">
          <Space size={12}>
            <Typography.Text>统计月份</Typography.Text>
            <DatePicker.MonthPicker value={runtime.draftMonth} onChange={runtime.setDraftMonth}
              allowClear={false} disabled={runtime.isExporting} style={{ width: 170 }} />
            <Button type="primary" icon={<Search size={16} />} onClick={runtime.search}
              loading={query.isFetching} disabled={!runtime.draftMonth || runtime.isExporting}>查询</Button>
          </Space>
          <Typography.Text type="secondary">按实际记录人统计 · 无业绩显示 0</Typography.Text>
        </div>
      </Card>
      {query.isError && <Alert type="error" showIcon content="绩效查询失败，请稍后重试。"
        action={<Button size="small" onClick={() => void query.refetch()}>重试</Button>} />}
      {runtime.exportError && <Alert type="error" showIcon content="Excel 导出失败，请稍后重试。" />}
      <Card bordered={false} title={<Space><span>{runtime.month} 月度绩效</span>
        {report && <Tag>{report.rows.length} 人</Tag>}</Space>}
        extra={canExport && <Button icon={<Download size={16} />} onClick={runtime.exportFile}
          loading={runtime.isExporting} disabled={!report}>导出 Excel</Button>}>
        <Table<PerformanceRow> rowKey="user_id" columns={columns} data={report?.rows ?? []}
          loading={query.isFetching} pagination={false} border={{ cell: true }}
          scroll={{ x: 1410, y: "calc(100vh - 385px)" }}
          noDataElement={<Empty description={query.isError ? "请重试查询" : "暂无启用的销售部人员"} />} />
        <Typography.Paragraph type="secondary" className="sales-performance-note">
          仅展示当前启用的销售部及下级部门人员。缺少记录人或无法匹配有效团期的数据不计入个人绩效。
          礼品沿用旧报表的计次及日期边界，其余指标按北京时间自然月统计。
          点击数字可查询和导出明细。
        </Typography.Paragraph>
      </Card>
      <SalesPerformanceDetails runtime={runtime} canExport={canExport} />
    </section>
  );
}
